// Ordered List Question Component
// Handles user interaction for ordered list trivia questions
// Users must arrange items in the correct order
// Follows the Section, Component & Manager System architecture

import React, { useState, useEffect } from 'react';

export function OrderedListQuestion({
  trivia,
  orderedItems,
  showResults,
  onOrderChange,
  onSubmitAnswer,
  disabled = false
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localOrder, setLocalOrder] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [movedIndex, setMovedIndex] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);

  // Initialize or update local order when trivia changes
  useEffect(() => {
    if (trivia.questionData?.items) {
      if (orderedItems && orderedItems.length > 0) {
        setLocalOrder(orderedItems);
      } else if (trivia.questionData.shuffleItems) {
        // Shuffle items for initial display
        const shuffled = [...trivia.questionData.items]
          .sort(() => Math.random() - 0.5)
          .map(item => item.id);
        setLocalOrder(shuffled);
      } else {
        // Use original order
        setLocalOrder(trivia.questionData.items.map(item => item.id));
      }
    }
  }, [trivia.questionData?.items, trivia.questionData?.shuffleItems, orderedItems]);

  const handleSubmit = async () => {
    if (!localOrder || localOrder.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmitAnswer(localOrder);
    } finally {
      setIsSubmitting(false);
    }
  };

  const moveItem = (fromIndex, toIndex) => {
    if (showResults || disabled) return;

    const newOrder = [...localOrder];
    const [movedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedItem);

    setLocalOrder(newOrder);
    onOrderChange(newOrder);
  };

  const getItemById = (id) => {
    return trivia.questionData?.items?.find(item => item.id === id);
  };

  const isOrderCorrect = () => {
    if (!localOrder || !trivia.questionData?.items) return false;

    return localOrder.every((itemId, index) => {
      const item = getItemById(itemId);
      return item && item.correctOrder === index;
    });
  };

  const handleDragStart = (e, index) => {
    if (showResults || disabled) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    if (showResults || disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    if (showResults || disabled || draggedIndex === null) return;
    e.preventDefault();

    if (draggedIndex !== dropIndex) {
      moveItem(draggedIndex, dropIndex);
      // Show feedback: moved item (red) and target item (green)
      setMovedIndex(draggedIndex);
      setTargetIndex(dropIndex);
      // Clear feedback after 1.5 seconds
      setTimeout(() => {
        setMovedIndex(null);
        setTargetIndex(null);
      }, 1500);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  if (!trivia.questionData?.items) {
    return <div className="ordered-list-error">Question data is missing</div>;
  }

  return (
    <div className="learn-trivia-options">
      <div className="ordered-list-container">
        <div className="ordered-list-instructions">
          <small>Arrange the items in the correct order</small>
        </div>

        <div className="ol-items-container">
          {localOrder.map((itemId, index) => {
            const item = getItemById(itemId);
            if (!item) return null;

            const isCorrectPosition = showResults && item.correctOrder === index;

            return (
              <div
                key={itemId}
                className={`ol-item-card ${
                  showResults ? (isCorrectPosition ? 'correct' : 'incorrect') :
                  index === movedIndex ? 'moved-item' :
                  index === targetIndex ? 'target-item' : ''
                } ${index === dragOverIndex ? 'drag-over' : ''}`}
                draggable={!showResults && !disabled}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                <div className="ol-drag-handle">
                  <div className="ol-grip">
                    <i className="fa-solid fa-grip-vertical"></i>
                  </div>
                </div>

                <div className="ol-item-content">
                  <div className="ol-item-header">
                    {trivia.questionData.showNumbers && (
                      <span className="ol-number">{index + 1}</span>
                    )}
                    <span className="ol-text">{item.text}</span>
                  </div>
                </div>

                <div className="ol-item-status">
                  {showResults && (
                    <div className="ol-result-indicator">
                      {isCorrectPosition ? (
                        <div className="ol-correct-badge">
                          <i className="fa-solid fa-check"></i>
                        </div>
                      ) : (
                        <div className="ol-position-hint">
                          Should be #{item.correctOrder + 1}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!showResults && (
          <div className="learn-submit-section">
            <button
              className="learn-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  Submitting...
                </>
              ) : (
                'Submit Order'
              )}
            </button>
          </div>
        )}

        {showResults && (
          <div className="learn-trivia-result">
            <div className="ordered-list-results">
              <div className="result-summary">
                <strong>Your ordering: {isOrderCorrect() ? 'Correct!' : 'Incorrect'}</strong>
              </div>
            </div>
            <div className="learn-explanation">
              <strong>Explanation:</strong> {trivia.explanation}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}